import { Component, EventEmitter, Injector, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractComponent,
  Channel,
  ChannelHealthClassificationEnum,
  ChannelHealthService,
  ChannelSummaryResponse,
} from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { translateIssueTitle } from '../channel-health-i18n.util';

/**
 * "Resumo" area (SPEC-2026-08-channel-health, T-21 — RF-17…RF-21).
 * State banner, Meta info card, connection card, Meta usage of the month (free service-message
 * allowance — `health-meta-usage-card`), 30d usage KPIs, daily sent×delivered chart.
 */
@Component({
    selector: 'health-summary-tab',
    templateUrl: './health-summary-tab.component.html',
    styleUrl: './health-summary-tab.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HealthSummaryTabComponent extends AbstractComponent implements OnInit, OnChanges {

  @Input({ required: true }) channel: Channel;
  @Input() refreshTick: number = 0;
  @Output() goToHealth = new EventEmitter<void>();

  summary?: ChannelSummaryResponse;
  chartData: Array<{ name: string, series: Array<{ name: string, value: number }> }> = [];

  classification = ChannelHealthClassificationEnum;

  constructor(
    public injector: Injector,
    private channelHealthService: ChannelHealthService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['refreshTick'] && !changes['refreshTick'].firstChange) {
      this.load();
    }
  }

  private load(): void {
    this.loadingContent = true;
    this.channelHealthService.getSummary(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (summary) => {
          this.summary = summary;
          this.chartData = (summary.series ?? []).map(point => ({
            name: this.shortDay(point.day),
            series: [
              { name: this.t('channel.health.summary.chartSent'), value: point.sent ?? 0 },
              { name: this.t('channel.health.summary.chartDelivered'), value: point.delivered ?? 0 },
            ],
          }));
          this.loadingContent = false;
        },
        error: (err) => {
          this.loadingContent = false;
          this.alertService.error(err?.error?.message);
        },
      });
  }

  /** The KPI card's `total` input is a string. */
  formatCount(value: number | null | undefined): string {
    return (value ?? 0).toLocaleString('pt-BR');
  }

  /** '2026-09-04' → '04/09'. */
  private shortDay(day: string): string {
    const parts = (day ?? '').split('-');
    return parts.length === 3 ? `${parts[2]}/${parts[1]}` : day;
  }

  bannerClassification(): ChannelHealthClassificationEnum {
    return this.summary?.banner?.classification ?? ChannelHealthClassificationEnum.NO_DATA;
  }

  /** System Bootstrap alert variants. */
  bannerAlertClass(): string {
    const map: Record<string, string> = {
      [ChannelHealthClassificationEnum.CRITICAL]: 'alert-danger',
      [ChannelHealthClassificationEnum.ATTENTION]: 'alert-warning',
      [ChannelHealthClassificationEnum.HEALTHY]: 'alert-success',
      [ChannelHealthClassificationEnum.NO_DATA]: 'alert-secondary',
    };
    return map[this.bannerClassification()];
  }

  /** RF-17: classification + practical consequence in plain language. */
  bannerText(): string {
    const pending = this.summary?.banner?.mainPendingIssue;
    const issue = pending ? translateIssueTitle((key, params) => this.t(key, params), pending) : undefined;
    const byClassification: Record<string, string> = {
      [ChannelHealthClassificationEnum.CRITICAL]: this.t('channel.health.summary.bannerCritical'),
      [ChannelHealthClassificationEnum.ATTENTION]: this.t('channel.health.summary.bannerAttention'),
      [ChannelHealthClassificationEnum.HEALTHY]: this.t('channel.health.summary.bannerHealthy'),
      [ChannelHealthClassificationEnum.NO_DATA]: this.t('channel.health.summary.bannerNoData'),
    };
    const base = byClassification[this.bannerClassification()];
    return issue && this.bannerClassification() !== ChannelHealthClassificationEnum.HEALTHY
      ? `${base} — ${issue}`
      : base;
  }

  numberStatusLabel(): string {
    const status = (this.summary?.metaInfo?.numberStatus ?? '').toUpperCase();
    const known = ['CONNECTED', 'DISCONNECTED', 'FLAGGED', 'RESTRICTED', 'PENDING', 'OFFLINE', 'RATE_LIMITED'];
    return known.includes(status) ? this.t(`channel.health.numberStatus.${status}`) : (status || '—');
  }

  qualityLabel(): string {
    const band = (this.summary?.metaInfo?.qualityRating ?? '').toUpperCase();
    const map: Record<string, string> = {
      GREEN: 'channel.health.quality.high',
      YELLOW: 'channel.health.quality.medium',
      RED: 'channel.health.quality.low',
    };
    return map[band] ? this.t(map[band]) : '—';
  }

  /** Meta tier → conversations per 24h, in plain words. */
  messagingLimitLabel(): string {
    const tier = (this.summary?.metaInfo?.messagingLimit ?? '').toUpperCase();
    const map: Record<string, string> = {
      TIER_50: '50',
      TIER_250: '250',
      TIER_1K: '1.000',
      TIER_10K: '10.000',
      TIER_100K: '100.000',
    };
    if (tier === 'TIER_UNLIMITED') return this.t('channel.health.summary.limitUnlimited');
    return map[tier] ? this.t('channel.health.summary.limitPerDay', { count: map[tier] }) : (tier || '—');
  }

  nameStatusLabel(): string {
    if ((this.summary?.metaInfo?.newNameStatus ?? '').toUpperCase() === 'PENDING_REVIEW') {
      return this.t('channel.health.nameStatus.NEW_PENDING');
    }
    const status = (this.summary?.metaInfo?.nameStatus ?? '').toUpperCase();
    const known = ['APPROVED', 'PENDING_REVIEW', 'DECLINED', 'EXPIRED', 'AVAILABLE_WITHOUT_REVIEW', 'NONE'];
    return known.includes(status) ? this.t(`channel.health.nameStatus.${status}`) : (status || '—');
  }

  modeLabel(): string {
    const mode = (this.summary?.connection?.mode ?? '').toUpperCase();
    if (mode === 'PRODUCTION') return this.t('channel.health.summary.modeProduction');
    if (mode === 'DEVELOPMENT') return this.t('channel.health.summary.modeDevelopment');
    return '—';
  }

  throughputLabel(): string {
    const mps = this.summary?.connection?.messagesPerSecond;
    return mps ? this.t('channel.health.summary.throughputValue', { mps }) : '—';
  }

  countryLabel(): string {
    const code = this.summary?.metaInfo?.countryCode;
    return code ? this.t(`channel.health.country.${code}`) : '';
  }
}
